import React, { useEffect, useRef, useState } from 'react'
import { getHomeworkVoiceRecordAudio } from '../services/education/homework.service'

interface VoiceRecordPlayerProps {
  url: string
  durationSeconds: number
}

function formatSeconds(value: number) {
  const total = Math.max(0, Math.floor(value))
  const minutes = String(Math.floor(total / 60)).padStart(2, '0')
  const seconds = String(total % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
}

export function VoiceRecordPlayer({ url, durationSeconds }: VoiceRecordPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(durationSeconds)
  const [failed, setFailed] = useState(false)
  const [loading, setLoading] = useState(false)
  const objectUrl = useRef<string | null>(null)
  const requestVersion = useRef(0)

  useEffect(() => {
    setPlaying(false)
    setCurrentTime(0)
    setDuration(durationSeconds)
    setFailed(false)
    setLoading(false)
    const audio = audioRef.current
    return () => {
      requestVersion.current += 1
      audio?.pause()
      audio?.removeAttribute('src')
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current)
      objectUrl.current = null
    }
  }, [url, durationSeconds])

  async function togglePlay() {
    const audio = audioRef.current
    if (!audio || loading) return

    if (playing) {
      audio.pause()
      return
    }

    const version = requestVersion.current
    setFailed(false)
    setLoading(true)
    try {
      if (!objectUrl.current) {
        const blob = await getHomeworkVoiceRecordAudio(url)
        if (version !== requestVersion.current) return
        objectUrl.current = URL.createObjectURL(blob)
        audio.src = objectUrl.current
      }
      if (audio.ended) audio.currentTime = 0
      await audio.play()
    } catch {
      if (version === requestVersion.current) setFailed(true)
    } finally {
      if (version === requestVersion.current) setLoading(false)
    }
  }

  const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0

  return (
    <div className={'voice-record-player'}>
      <button type={'button'} className={'play-btn'} disabled={loading} onClick={togglePlay}
        aria-label={loading ? 'Ses kaydı yükleniyor' : failed ? 'Ses kaydını tekrar aç' : playing ? 'Ses kaydını duraklat' : 'Ses kaydını oynat'}>
        <i className={playing ? 'fa-solid fa-pause' : 'fa-solid fa-play'}></i>
      </button>
      <div className={'track'}>
        <div className={'progress-line'}>
          <div className={'progress-value'} style={{ width: progress + '%' }}></div>
        </div>
        <span className={'time'}>
          {loading ? 'Yükleniyor…' : failed
            ? 'Kayıt açılamadı, tekrar deneyin'
            : formatSeconds(currentTime) + ' / ' + formatSeconds(duration)}
        </span>
      </div>
      <audio
        ref={audioRef}
        preload={'none'}
        onLoadedMetadata={(event) => {
          const value = event.currentTarget.duration
          if (Number.isFinite(value) && value > 0) setDuration(value)
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onPlay={() => {
          document.querySelectorAll('audio').forEach(item => {
            if (item !== audioRef.current) item.pause()
          })
          setPlaying(true)
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false)
          setCurrentTime(0)
        }}
        onError={() => setFailed(true)}
      />
    </div>
  )
}
